import { useNavigate } from 'react-router-dom';
import { useWeekEvents } from '../hooks/useEventData';
import { useWeekAvailability, useWeeklyPlan, usePlanFeedback } from '../hooks/useAvailability';
import { useAuth } from '../context/AuthContext';
import { formatTime, getWeekDates, isToday } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';
import AvailabilityToggle from '../components/AvailabilityToggle';
import AvailabilityBanner from '../components/AvailabilityBanner';
import AdminAvailabilitySummary from '../components/AdminAvailabilitySummary';
import WeeklyPlanPanel from '../components/WeeklyPlanPanel';
import PlanFeedbackPanel from '../components/PlanFeedbackPanel';
import { useGroupData } from '../hooks/useGroupData';
import type { CalendarEvent, EventType, AvailabilityStatus } from '../types';

const gradients: Record<EventType, string> = {
  practice: 'from-sky-400 to-cyan-500 dark:from-sky-600 dark:to-cyan-700',
  regatta: 'from-purple-500 to-fuchsia-500 dark:from-purple-700 dark:to-fuchsia-700',
  competition: 'from-amber-400 to-orange-500 dark:from-amber-600 dark:to-orange-700',
};

export default function WeeklyViewC() {
  const { events, loading, error } = useWeekEvents();
  const { user } = useAuth();
  const navigate = useNavigate();
  const weekDates = getWeekDates();

  const activeEvents = events.filter(e => e.status === 'on');
  const { availability, setMyAvailability } = useWeekAvailability(activeEvents);
  const { plan, generating, generate, share, publish, editAssignment } = useWeeklyPlan();
  const { feedback, summary: feedbackSummary, submitting: feedbackSubmitting, submit: submitFeedback } = usePlanFeedback(plan?.id || null);
  const myFeedback = feedback.find(f => f.userId === user?.id) || null;

  const eventsByDate = new Map<string, CalendarEvent>();
  events.forEach(e => eventsByDate.set(e.date, e));

  const isAdmin = user?.role === 'admin';
  const { members } = useGroupData();

  const missingCount = activeEvents.filter(e => {
    const records = availability[e.id] || [];
    return !records.some(r => r.userId === user?.id);
  }).length;

  const handleAvailability = async (eventId: string, status: AvailabilityStatus) => {
    await setMyAvailability(eventId, status);
  };

  if (loading) return <LoadingSpinner className="mt-20" />;
  if (error) return <div className="p-6 text-center text-cancelled">{error}</div>;

  return (
    <div className="px-5 pt-6 pb-6 max-w-lg mx-auto">
      <h1 className="text-3xl font-black text-gray-900 dark:text-white">
        This Week
      </h1>
      <p className="text-gray-400 mt-0.5">
        {new Date(weekDates[0] + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
        {' - '}
        {new Date(weekDates[6] + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
      </p>

      <div className="mt-4">
        <AvailabilityBanner missingCount={missingCount} />
        {isAdmin && <WeeklyPlanPanel plan={plan} generating={generating} onGenerate={generate} onShare={share} onPublish={publish} feedback={feedback} feedbackSummary={feedbackSummary} members={members} onEditAssignment={editAssignment} />}
        {!isAdmin && plan && plan.status === 'pending_review' && (
          <PlanFeedbackPanel plan={plan} myFeedback={myFeedback} submitting={feedbackSubmitting} onSubmit={submitFeedback} />
        )}
      </div>

      <div className="mt-2 grid gap-3">
        {weekDates.map(date => {
          const d = new Date(date + 'T00:00:00');
          const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
          const dayNum = d.getDate();
          const event = eventsByDate.get(date);
          const today = isToday(date);

          // Event day — bold card
          if (event && event.status !== 'cancelled') {
            const myAvail = (availability[event.id] || []).find(r => r.userId === user?.id)?.status || null;

            return (
              <div key={date} className={`rounded-3xl bg-gradient-to-br ${gradients[event.type]} shadow-lg relative overflow-hidden`}>
                <button
                  onClick={() => navigate(`/event/${event.id}`)}
                  className="w-full p-5 text-white text-left transition-transform active:scale-[0.98] min-h-tap"
                >
                  {/* Decorative */}
                  <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full" />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs font-black uppercase tracking-widest opacity-70">{dayName}</p>
                        <p className="text-3xl font-black leading-none">{dayNum}</p>
                      </div>
                      <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-black uppercase">
                        {event.type}
                      </span>
                    </div>

                    {event.title && <p className="font-bold opacity-90 mb-1">{event.title}</p>}

                    <div className="flex items-center gap-3 mt-2 text-sm opacity-90">
                      <span className="font-bold">{formatTime(event.startTime)}-{formatTime(event.endTime)}</span>
                      <span>&bull;</span>
                      <span className="truncate">{event.locationName}</span>
                    </div>
                  </div>

                  {today && (
                    <div className="absolute top-3 right-3 w-3 h-3 bg-white rounded-full animate-pulse" />
                  )}
                </button>

                {/* Availability overlay */}
                <div className="px-5 pb-4 pt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-white/60 font-semibold">Can you drive?</span>
                    <AvailabilityToggle status={myAvail} onChange={(s) => handleAvailability(event.id, s)} compact />
                  </div>
                  {isAdmin && (
                    <div className="mt-2 bg-black/20 backdrop-blur-sm rounded-xl p-3">
                      <AdminAvailabilitySummary members={members} records={availability[event.id] || []} onDark />
                    </div>
                  )}
                </div>
              </div>
            );
          }

          // Cancelled event
          if (event && event.status === 'cancelled') {
            return (
              <div key={date} className="rounded-3xl bg-cancelled/5 dark:bg-cancelled/10 border border-cancelled/20 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-black uppercase tracking-widest text-gray-400">{dayName}</p>
                    <p className="text-2xl font-black text-gray-300 dark:text-gray-600 leading-none">{dayNum}</p>
                  </div>
                  <span className="text-xs font-black text-cancelled bg-cancelled/10 px-3 py-1 rounded-full uppercase">Cancelled</span>
                </div>
              </div>
            );
          }

          // No event — minimal
          return (
            <div key={date} className={`rounded-2xl px-4 py-3 ${today ? 'bg-gray-50 dark:bg-gray-900' : ''}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-xs font-bold uppercase tracking-widest ${
                    today ? 'text-practice dark:text-practice-light' : 'text-gray-300 dark:text-gray-700'
                  }`}>{dayName}</p>
                  <p className={`text-2xl font-black leading-none ${
                    today ? 'text-gray-900 dark:text-white' : 'text-gray-200 dark:text-gray-800'
                  }`}>{dayNum}</p>
                </div>
                <span className="text-xs text-gray-300 dark:text-gray-700 font-medium">No event</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
